// Import service instances and utilities first
import { DashboardStats, ChartDataPoint, ApiResponse } from '../../types/api'
import { apiClient } from '../../utils/api'
import { authService } from './auth'
import { invoiceService } from './invoice'
import { customerService } from './customer'
import { productService } from './product'
import { fileService } from './file'
import { zatcaService } from './zatca'
import { columnConfigService } from './columnConfig'
import { salesService } from './sales'

// Export all API services
export { invoiceService, InvoiceService } from './invoice'
export { customerService, CustomerService } from './customer'
export { productService, ProductService } from './product'
export { authService, AuthService } from './auth'
export { fileService, FileService } from './file'
export { zatcaService, ZatcaService } from './zatca'
export { columnConfigService, ColumnConfigService } from './columnConfig'
export { salesService, SalesService } from './sales'

// Export API utilities
export { 
  apiClient, 
  ApiClient, 
  setAuthToken, 
  getAuthToken, 
  clearAuthToken, 
  handleApiError, 
  isApiError, 
  extractData, 
  extractMessage, 
  withRetry, 
  createFormData 
} from '../../utils/api'

// Re-export the ApiError class with a different name to avoid conflicts
export { ApiError as ApiErrorClass } from '../../utils/api'

// Export types from api types file
export type { 
  ApiResponse, 
  PaginatedResponse, 
  ApiError as ApiErrorInterface, 
  ValidationError,
  Customer,
  Product,
  Invoice,
  InvoiceStatus,
  LineItem,
  PaymentMethod,
  InvoiceFilters,
  CustomerFilters,
  ProductFilters,
  FileAttachment,
  FileCategory,
  ZatcaStatus,
  ZatcaError,
  ZatcaWarning,
  User,
  UserRole,
  Branch,
  LoginCredentials,
  AuthResponse,
  DashboardStats,
  ChartDataPoint,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  UploadFileRequest,
  SelectOption,
  TableColumn,
  PaginationParams
} from '../../types/api'

// Dashboard Service
export class DashboardService {
  private readonly basePath = '/dashboard'

  async getStats(period?: 'today' | 'week' | 'month' | 'quarter' | 'year'): Promise<ApiResponse<DashboardStats>> {
    return apiClient.get(`${this.basePath}/stats`, { period })
  }

  async getSalesChart(period: string, groupBy: 'day' | 'week' | 'month'): Promise<ApiResponse<ChartDataPoint[]>> {
    return apiClient.get(`${this.basePath}/sales-chart`, { period, group_by: groupBy })
  }

  async getRevenueChart(period: string): Promise<ApiResponse<ChartDataPoint[]>> {
    return apiClient.get(`${this.basePath}/revenue-chart`, { period })
  }

  async getTopProducts(limit: number = 5): Promise<ApiResponse<Array<{
    product: any
    quantity_sold: number
    revenue: number
  }>>> {
    return apiClient.get(`${this.basePath}/top-products`, { limit })
  }

  async getTopCustomers(limit: number = 5): Promise<ApiResponse<Array<{
    customer: any
    total_amount: number
    invoice_count: number
  }>>> {
    return apiClient.get(`${this.basePath}/top-customers`, { limit })
  }

  async getRecentActivity(limit: number = 10): Promise<ApiResponse<Array<{
    id: string
    type: 'invoice_created' | 'invoice_paid' | 'customer_added' | 'product_updated'
    description: string
    user: string
    timestamp: string
    metadata?: Record<string, any>
  }>>> {
    return apiClient.get(`${this.basePath}/recent-activity`, { limit })
  }

  async getAlerts(): Promise<ApiResponse<Array<{
    id: string
    type: 'warning' | 'error' | 'info'
    title: string
    message: string
    action_required: boolean
    created_at: string
    action_url?: string
  }>>> {
    return apiClient.get(`${this.basePath}/alerts`)
  }

  async dismissAlert(alertId: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/alerts/${alertId}`)
  }

  async getSystemOverview(): Promise<ApiResponse<{
    total_invoices: number
    total_customers: number
    total_products: number
    monthly_revenue: number
    growth_rate: number
    system_health: 'good' | 'warning' | 'critical'
    active_users: number
    storage_used: number
    storage_total: number
  }>> {
    return apiClient.get(`${this.basePath}/system-overview`)
  }
}

export const dashboardService = new DashboardService()

// Unified API object
export const api = {
  auth: authService,
  invoices: invoiceService,
  customers: customerService,
  products: productService,
  files: fileService,
  zatca: zatcaService,
  columnConfig: columnConfigService,
  sales: salesService,
  dashboard: dashboardService,
}

export default api 